import { useEffect, useState, type FormEvent } from 'react'
import { ApiError, api, branding as brandingApi, type SignInResponse } from '../../lib/api'
import { applyBrand, brandLookup, brandQuery, type PublicBranding } from '../../lib/branding'
import { orgFromPath } from '../../lib/orgPath'
import { PRODUCT_ASSETS, PRODUCT_NAME, PRODUCT_TAGLINE } from '../../lib/product'

/**
 * The one screen that is branded before anybody has signed in.
 *
 * It reads `GET /api/v1/branding` without a token, which is why two properties of that endpoint are
 * load-bearing rather than incidental. It answers identically for an organization that does not exist
 * and one that has chosen nothing, so this page cannot be used to find out who is a customer - the
 * sign-in handler itself is deliberately non-enumerating and a branded sign-in page would otherwise
 * hand that oracle back one level up. And it returns four values and nothing else about the
 * organization, so there is no name to render here even if there were a place for it.
 *
 * The DOM contract is unchanged and is deliberately unchanged: `#tenant`, `#email`, `#password` and
 * the submit button are bound by `tests/e2e/specs/fixtures.ts`, through which every specification in
 * the suite signs in. Branding is added around the form, never to it. The one exception is deliberate:
 * at `/{organization}/sign-in` there is no `#tenant`, because the address already names the
 * organization; the fixtures sign in at the unprefixed `/sign-in`, where it is still there.
 */
export function LoginPage({
  onSignedIn,
  ended = false
}: {
  onSignedIn: (identity: SignInResponse) => void
  /**
   * Whether somebody is here because their session ran out, rather than because they arrived.
   *
   * Only ever true for a session that expired. Arriving with no session at all is the ordinary way
   * to reach a sign-in page and needs no explanation, and a wrong password keeps the server's own
   * non-enumerating message, which this must never replace.
   */
  ended?: boolean
}) {
  // The organization in the address when there is one. A sign-in link written as
  // `/fleettest/sign-in` has already said which organization it is for, so the field is not shown at
  // all; the page names the organization instead and links to the unprefixed page for somebody who
  // followed the wrong link.
  const [fromAddress] = useState(() => orgFromPath(window.location.pathname))
  const [tenantSlug, setTenantSlug] = useState(fromAddress ?? 'development')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [failure, setFailure] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [brand, setBrand] = useState<PublicBranding | null>(null)
  const [lookup, setLookup] = useState(() => brandLookup(window.location.hostname, fromAddress))

  /**
   * Asked for by host on arrival, and again by organization once somebody has named one.
   *
   * A failure is silent on purpose. Branding is decoration on this screen and the one thing that
   * must always work here is the form; an organization whose color could not be read should still be
   * able to sign in, and should not be told about it in a banner above the password field.
   */
  useEffect(() => {
    if (!lookup) {
      setBrand(null)
      return
    }

    let current = true
    brandingApi
      .publicBranding(brandQuery(lookup))
      .then((next) => {
        if (current) setBrand(next)
      })
      .catch(() => {
        if (current) setBrand(null)
      })

    return () => {
      current = false
    }
  }, [lookup])

  useEffect(() => {
    applyBrand(document.documentElement, brand?.configured ? brand.brandAccent : null)
  }, [brand])

  async function submit(event: FormEvent) {
    event.preventDefault()
    setFailure(null)
    setBusy(true)
    try {
      onSignedIn(await api.signIn(tenantSlug.trim(), email.trim(), password))
    } catch (error) {
      // The server returns one message for every failure, deliberately. Repeating it verbatim keeps
      // the SPA from becoming the account-enumeration oracle the API refused to be.
      setFailure(error instanceof ApiError ? error.message : 'Sign-in failed. Try again.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="sign-in">
      <div className="sign-in-stage">
        {/*
          The product's art, carrying the tagline, beside the form on a wide screen only. The art is
          960 by 1219 and has nothing to say that the logo above the form does not, so a phone is
          given the logo and spared the download's worth of scrolling (D-514).
        */}
        <img
          className="sign-in-art"
          data-testid="sign-in-art"
          src={PRODUCT_ASSETS.signInArt}
          alt={`${PRODUCT_NAME} - ${PRODUCT_TAGLINE}`}
          width={960}
          height={1219}
        />

        <div className="sign-in-column">
          {/* Both variants are in the page and the palette decides which one shows, because the
              palette is an attribute on the document and a <picture> can only ask the system. */}
          <div className="sign-in-logo" data-testid="sign-in-logo">
            <img
              className="sign-in-logo-light"
              src={PRODUCT_ASSETS.logoLight}
              alt={PRODUCT_NAME}
              width={265}
              height={80}
            />
            <img
              className="sign-in-logo-dark"
              src={PRODUCT_ASSETS.logoDark}
              alt={PRODUCT_NAME}
              width={265}
              height={80}
            />
          </div>

          <div className="card">
            <form className="card-body" onSubmit={submit}>
              {brand?.hasBrandMark && lookup && (
                <img
                  className="sign-in-mark"
                  data-testid="sign-in-mark"
                  src={brandingApi.markUrl(brandQuery(lookup))}
                  alt=""
                />
              )}

              <h1 style={{ marginTop: 0 }}>Sign in to {PRODUCT_NAME}</h1>
              <p className="muted" style={{ marginTop: 0 }}>
                Manage the policies your fleet runs.
              </p>

              {failure && (
                <div className="banner error" role="alert">
                  {failure}
                </div>
              )}

              {/*
                Below any refusal, never instead of one: if somebody has just typed the wrong password on
                this screen, that is the more useful of the two sentences and must stay first.
              */}
              {ended && !failure && (
                <div className="banner" role="status" data-testid="session-ended">
                  Your session ended because nothing had happened on it for a while. Sign in to carry on from
                  where you were.
                </div>
              )}

              {/*
                The organization is already in the address, so asking for it again would be asking
                somebody to retype what the link they followed says. Named instead, with a way out for
                whoever followed the wrong link: the unprefixed page, where the field is back. A plain
                anchor rather than a router link, because leaving the organization segment means
                leaving the router's basename.
              */}
              {fromAddress ? (
                <p className="hint" data-testid="sign-in-organization">
                  Signing in to <strong>{fromAddress}</strong>. <a href="/sign-in">Not your organization?</a>
                </p>
              ) : (
                <div className="field">
                  <label htmlFor="tenant">Organization ID</label>
                  {/* Named as an ID rather than "Organization", because the field wants the short
                      identifier and people typed their company's full name into it. */}
                  <p className="hint" id="tenant-hint">
                    The short ID your administrator gave you, not your organization&rsquo;s full name.
                  </p>
                  <input
                    id="tenant"
                    name="tenantSlug"
                    aria-describedby="tenant-hint"
                    autoComplete="organization"
                    value={tenantSlug}
                    onChange={(event) => setTenantSlug(event.target.value)}
                    // On blur rather than on every keystroke: the lookup is a request, and one per
                    // character typed into an organization field would be a request per character.
                    onBlur={() => setLookup(brandLookup(window.location.hostname, tenantSlug))}
                    required
                  />
                </div>
              )}

              <div className="field">
                <label htmlFor="email">Your email address</label>
                <p className="hint" id="email-hint">
                  This is your sign-in name.
                </p>
                <input
                  id="email"
                  name="email"
                  type="email"
                  aria-describedby="email-hint"
                  autoComplete="username"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  required
                />
              </div>

              <div className="field">
                <label htmlFor="password">Password</label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  required
                />
              </div>

              <div className="field">
                <button type="submit" className="primary" disabled={busy}>
                  {busy ? 'Signing in…' : 'Sign in'}
                </button>
              </div>

              {brand?.reportFooter && (
                <p className="sign-in-footer" data-testid="sign-in-footer">
                  {brand.reportFooter}
                </p>
              )}
            </form>
          </div>
        </div>
      </div>
    </div>
  )
}
